/**
 * Child Life Chapters - Constants and Utility Functions
 *
 * Age-band definitions, chapter definitions per band, and helpers
 * for computing a child's current age band from birth data.
 */

// ---------------------------------------------------------------------------
// Age band definitions
// ---------------------------------------------------------------------------

export const AGE_BANDS = {
  infant: { key: 'infant', label: 'Infant', minAge: 0, maxAge: 3, description: 'Ages 0\u20133' },
  young_child: { key: 'young_child', label: 'Young Child', minAge: 4, maxAge: 8, description: 'Ages 4\u20138' },
  tween: { key: 'tween', label: 'Tween', minAge: 9, maxAge: 12, description: 'Ages 9\u201312' },
  teen: { key: 'teen', label: 'Teen', minAge: 13, maxAge: 17, description: 'Ages 13\u201317' },
  adult: { key: 'adult', label: 'Adult', minAge: 18, maxAge: 999, description: 'Ages 18+' },
};

// ---------------------------------------------------------------------------
// Chapter definitions per band
// ---------------------------------------------------------------------------

export const CHAPTER_DEFINITIONS = {
  infant: [
    { index: 1, title: 'Welcome to the World', emoji: '\uD83C\uDF05', description: 'Birth story \u2014 the day, the room, the first cry, the first hold', authorVoice: 'guardian_only' },
    { index: 2, title: 'First Days at Home', emoji: '\uD83C\uDFE0', description: 'Coming home, first nights, chaos and wonder', authorVoice: 'guardian_only' },
    { index: 3, title: 'Who They Already Are', emoji: '\u2728', description: 'Early personality \u2014 sounds, sleep, what calms them', authorVoice: 'guardian_only' },
    { index: 4, title: 'Our Family', emoji: '\uD83E\uDD1D', description: 'Who has already held them, grandparents, siblings, family context', authorVoice: 'guardian_only' },
    { index: 5, title: 'Letters to the Future', emoji: '\u2709\uFE0F', description: 'Messages from today for the child to read someday', authorVoice: 'guardian_only' },
  ],
  young_child: [
    { index: 1, title: 'My Beginning', emoji: '\uD83C\uDF31', description: 'Name story, birth story, early memories', authorVoice: 'guardian_primary' },
    { index: 2, title: 'Play & Learning', emoji: '\uD83C\uDFA8', description: 'Preschool/school, discovery, how they learn through play', authorVoice: 'guardian_primary' },
    { index: 3, title: 'What I Love', emoji: '\u2B50', description: 'Favorites \u2014 food, toys, shows, songs, games', authorVoice: 'mixed' },
    { index: 4, title: 'Friends & Family', emoji: '\uD83E\uDD1D', description: 'Best friends, cousins, grandparents, bonds they\'re building', authorVoice: 'guardian_primary' },
    { index: 5, title: 'Growing Up', emoji: '\uD83D\uDE80', description: 'Milestones, firsts, what they\'re becoming, dreams', authorVoice: 'guardian_primary' },
  ],
  tween: [
    { index: 1, title: 'Who I Am', emoji: '\uD83E\uDE9E', description: 'Identity forming \u2014 values, personality, how they see themselves', authorVoice: 'mixed' },
    { index: 2, title: 'School Life', emoji: '\uD83D\uDCDA', description: 'Subjects, teachers, challenges, achievements', authorVoice: 'mixed' },
    { index: 3, title: 'Passions & Talents', emoji: '\uD83C\uDFAF', description: 'What they\'re good at, what they love doing, hobbies', authorVoice: 'child_leads' },
    { index: 4, title: 'My World', emoji: '\uD83C\uDF0D', description: 'Friends, community, social life, who matters to them', authorVoice: 'mixed' },
    { index: 5, title: 'Looking Ahead', emoji: '\uD83D\uDD2D', description: 'Dreams, plans, questions about the future', authorVoice: 'child_leads' },
  ],
  teen: [
    { index: 1, title: 'My Identity', emoji: '\uD83E\uDDED', description: 'Who I\'m becoming \u2014 values, beliefs, how I see the world', authorVoice: 'child_leads' },
    { index: 2, title: 'High School', emoji: '\uD83C\uDFEB', description: 'Experiences, memories, friendships, defining moments', authorVoice: 'child_leads' },
    { index: 3, title: 'Passions & Skills', emoji: '\uD83D\uDCA1', description: 'What I\'m developing, what I\'m proud of, what I want to pursue', authorVoice: 'child_leads' },
    { index: 4, title: 'My People', emoji: '\u2764\uFE0F', description: 'Friendships, bonds, relationships that matter', authorVoice: 'child_leads' },
    { index: 5, title: 'Future Dreams', emoji: '\uD83D\uDE80', description: 'Plans, aspirations, fears, hopes for what comes next', authorVoice: 'child_leads' },
  ],
};

// ---------------------------------------------------------------------------
// Band-to-band chapter mapping labels
// ---------------------------------------------------------------------------

export const BAND_TRANSITION_MAPPING = {
  infant_to_young_child: [
    { from: 'Welcome to the World', to: 'My Beginning' },
    { from: 'First Days at Home', to: 'Play & Learning' },
    { from: 'Who They Already Are', to: 'What I Love' },
    { from: 'Our Family', to: 'Friends & Family' },
    { from: 'Letters to the Future', to: 'Growing Up' },
  ],
  young_child_to_tween: [
    { from: 'My Beginning', to: 'Who I Am' },
    { from: 'Play & Learning', to: 'School Life' },
    { from: 'What I Love', to: 'Passions & Talents' },
    { from: 'Friends & Family', to: 'My World' },
    { from: 'Growing Up', to: 'Looking Ahead' },
  ],
  tween_to_teen: [
    { from: 'Who I Am', to: 'My Identity' },
    { from: 'School Life', to: 'High School' },
    { from: 'Passions & Talents', to: 'Passions & Skills' },
    { from: 'My World', to: 'My People' },
    { from: 'Looking Ahead', to: 'Future Dreams' },
  ],
};

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

/**
 * Calculate the current age band from a birth year and optional exact birth date.
 * If a full birth date is available it is used for precision; otherwise falls back
 * to the birth year compared against the current year.
 *
 * @param {number|string} birthYear  - 4-digit year (e.g. 2020)
 * @param {string|null}   birthDate  - ISO date string (e.g. "2020-06-15") or null
 * @returns {string|null} One of the AGE_BANDS keys, or null if input is invalid.
 */
export function calculateAgeBand(birthYear, birthDate) {
  let age;

  if (birthDate) {
    const born = new Date(birthDate);
    const now = new Date();
    age = now.getFullYear() - born.getFullYear();
    // Adjust if birthday hasn't occurred yet this year
    const monthDiff = now.getMonth() - born.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < born.getDate())) {
      age -= 1;
    }
  } else if (birthYear) {
    const year = typeof birthYear === 'string' ? parseInt(birthYear, 10) : birthYear;
    if (isNaN(year)) return null;
    age = new Date().getFullYear() - year;
  } else {
    return null;
  }

  if (age < 0) return null;

  for (const band of Object.values(AGE_BANDS)) {
    if (age >= band.minAge && age <= band.maxAge) {
      return band.key;
    }
  }

  return 'adult';
}

/**
 * Calculate a child's age in whole months from an exact birth date.
 *
 * @param {string} birthDate - ISO date string
 * @returns {number|null} Age in months, or null if birthDate is falsy.
 */
export function calculateAgeInMonths(birthDate) {
  if (!birthDate) return null;

  const born = new Date(birthDate);
  const now = new Date();

  let months = (now.getFullYear() - born.getFullYear()) * 12 + (now.getMonth() - born.getMonth());
  if (now.getDate() < born.getDate()) {
    months -= 1;
  }
  return Math.max(0, months);
}

/**
 * Get a human-friendly age display label.
 * Examples: "2 months old", "1 year old", "3 years old"
 *
 * @param {number|string} birthYear
 * @param {string|null}   birthDate
 * @returns {string}
 */
export function getAgeDisplayLabel(birthYear, birthDate) {
  if (birthDate) {
    const months = calculateAgeInMonths(birthDate);
    if (months === null) return '';
    if (months < 1) {
      // Calculate days
      const born = new Date(birthDate);
      const now = new Date();
      const diffMs = now - born;
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      return days === 1 ? '1 day old' : `${days} days old`;
    }
    if (months < 24) {
      return months === 1 ? '1 month old' : `${months} months old`;
    }
    const years = Math.floor(months / 12);
    return years === 1 ? '1 year old' : `${years} years old`;
  }

  if (birthYear) {
    const year = typeof birthYear === 'string' ? parseInt(birthYear, 10) : birthYear;
    if (isNaN(year)) return '';
    const age = new Date().getFullYear() - year;
    if (age < 1) return 'Less than 1 year old';
    return age === 1 ? '1 year old' : `${age} years old`;
  }

  return '';
}

/**
 * Determine whether a child is a newborn (less than 3 months old).
 *
 * @param {string|null} birthDate - ISO date string
 * @returns {boolean}
 */
export function isNewborn(birthDate) {
  if (!birthDate) return false;
  const months = calculateAgeInMonths(birthDate);
  return months !== null && months < 3;
}

/**
 * Return a user-friendly label for the author voice enum.
 *
 * @param {string} authorVoice - One of 'guardian_only', 'guardian_primary', 'mixed', 'child_leads'
 * @returns {{ label: string, icon: string }}
 */
export function getAuthorVoiceLabel(authorVoice) {
  switch (authorVoice) {
    case 'guardian_only':
      return { label: 'Guardian writes', icon: '\uD83D\uDC64' };
    case 'guardian_primary':
      return { label: 'Guardian leads', icon: '\uD83D\uDC64' };
    case 'mixed':
      return { label: 'Mixed authorship', icon: '\uD83E\uDD1D' };
    case 'child_leads':
      return { label: 'Child leads', icon: '\uD83D\uDC67' };
    default:
      return { label: 'Any author', icon: '\u270D\uFE0F' };
  }
}
